import { format, parseISO } from "date-fns"
import useSWR from "swr"
import { Redirect, useRoute } from "wouter-preact"

import { lookupRemoteReferences, RemoteReference } from "@/api/accounting.tsx"
import { REMOTE_DOC_LABEL, remote_reference_outcome, remote_reference_path } from "@/accounting/remote_reference"
import { CenterSpinner } from "@/app/loading.tsx"
import { exactCurrencyFormatter, usePageTitle } from "@/utils"

function DocumentRow({ reference }: { reference: RemoteReference }) {
  const path = remote_reference_path(reference)
  const facts = [
    reference.doc_number ? `#${reference.doc_number}` : null,
    `internal id ${reference.remote_id}`,
    reference.customer_name,
    reference.date ? format(parseISO(reference.date), "MMM d, yyyy") : null,
    reference.amount != null ? exactCurrencyFormatter(reference.amount) : null,
  ]
    .filter(Boolean)
    .join(" · ")
  return (
    <li class="py-2 text-sm text-gray-700">
      <span class="font-medium">{REMOTE_DOC_LABEL[reference.kind]}</span>
      <span class="ml-2">{facts}</span>
      {path && (
        <a href={path} class="ml-2 text-blue-600 underline">
          Open {reference.check_number && !reference.deduction_id ? `check ${reference.check_number}` : "deduction"}
        </a>
      )}
    </li>
  )
}

/**
 * `/netsuite/<internal id or document number>`: a link a NetSuite dashboard or saved search can
 * build from a transaction. It goes to the check or deduction behind the document, and says plainly
 * when there is none.
 */
export function NetsuiteDocumentPage() {
  usePageTitle("NetSuite document")
  const [, params] = useRoute("/netsuite/:id")
  const id = decodeURIComponent(params?.id ?? "")
  const { data: references, error } = useSWR(`/accounting/remote-reference?q=${id}`, () => lookupRemoteReferences(id))

  if (error) {
    return <p class="mx-4 mt-8 text-sm text-red-700">Could not look up NetSuite document {id}. Try again.</p>
  }
  if (!references) {
    return <CenterSpinner />
  }

  const outcome = remote_reference_outcome(references)
  if (outcome.kind === "go") {
    return <Redirect to={outcome.path} replace />
  }
  return (
    <div class="mx-4 mt-8 max-w-3xl rounded-lg border border-gray-200 bg-white p-6">
      <h1 class="text-lg font-semibold text-gray-900">NetSuite document {id}</h1>
      {outcome.kind === "none" && (
        <p class="mt-2 text-sm text-gray-600">
          Nothing in MarginWiz matches this internal ID or document number. It may be a transaction MarginWiz does
          not sync, or one newer than the last sync.
        </p>
      )}
      {outcome.kind === "unlinked" && (
        <p class="mt-2 text-sm text-gray-600">
          MarginWiz has this document from NetSuite, but did not create it, and nothing links it to a check or a
          deduction here.
        </p>
      )}
      {outcome.kind === "choose" && (
        <p class="mt-2 text-sm text-gray-600">This number matches more than one record.</p>
      )}
      {outcome.kind !== "none" && (
        <ul class="mt-3 divide-y divide-gray-100">
          {outcome.references.map((reference: RemoteReference) => (
            <DocumentRow key={`${reference.kind}:${reference.remote_id}:${remote_reference_path(reference)}`} reference={reference} />
          ))}
        </ul>
      )}
    </div>
  )
}
